import React, { useContext, useMemo } from "react";
import Title from "../../../components/Title";
import LineGraph from "../../../components/LineGraph";
import { gql, useQuery } from "@apollo/client";
import AppContext, { IFilters } from "../../../AppContext";
import { Entorno } from "../../../utils/constants";
import {
  getMax,
  getMedidaSQL,
  getMin,
  getSteps,
  mergeKeyData,
} from "../../../utils/functions";
import { MessageBar, Spinner, SpinnerSize } from "@fluentui/react";
import { useTranslation } from "react-i18next";

export const titleEn = "Trends";
export const title = `Indicadores de tendencia`;
export const filters: IFilters = {
  limitedPeriodo: true,
  showMedidas: true,
  hasTicketMedio: true,
  isDesglose: false,
  showEntorno: true,
  showSegmentos: false,
  showMyData: false,
};

const Graph = () => {
  const { semanas, filters, user } = useContext(AppContext);
  const medida = getMedidaSQL(filters.medidas);
  const { t, i18n } = useTranslation();
  const title = t("TrendsI") as string;

  const translateDataLabel = (label: string) => {
    const normalized = (label || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ");
    if (normalized === "mercado") return t("Market") as string;
    if (normalized === "cafe") return t("Cafe") as string;
    return label;
  };

  const entorno = filters.entorno === Entorno.Comparable ? "Comparables" : "Totales";

  const GET_GRAPH = gql`
    query ($yr1: Int!, $wk1: Int!, $yr2: Int!, $wk2: Int!, $var: Int) {
      graph(
        Graph: "Indicadores de tendencia"
        WeekRange: {
          Start: { Year: $yr1, Week: $wk1 }
          End: { Year: $yr2, Week: $wk2 }
        }
        Filters: { Variables: $var }
      )
    }
  `;

  const qry = useQuery(GET_GRAPH, {
    fetchPolicy: "no-cache",
    variables: {
      yr1: semanas[0].year,
      wk1: semanas[0].week,
      yr2: semanas[1].year,
      wk2: semanas[1].week,
      var: 1,
    },
  });

  let errorMsg = t("An-error-has-occur-when-recovering-data") as string;
  let loading = qry.loading || qry.data === undefined;
  let error = !semanas || !!qry.error;

  let labels: string[] = [];
  let datalabels: string[] = [];
  let values: number[][] = [];
  let series: any = [];

  let exportData: any[] = [];
  if (!loading && !error && qry.data?.graph) {
    try {
      const data = qry?.data?.graph;
      if (!data) throw new Error(errorMsg);
      if (data?.Error) throw new Error(`${data.Error}`);

      labels = data.map((d: any) => Object.keys(d)[0]);
      if (labels.length < 3) {
        throw new Error(
          t("There-is-not-enough-data-to-show-results-for-this-filter-selection") as string
        );
      }

      datalabels = data[0][labels[0]].map(
        (d: { Descripcion: string }) => d.Descripcion
      );

      for (let i = 0; i < datalabels.length; i++) {
        values.push(
          data
            .map((dat: any) =>
              Object.values(dat).map((d: any) => d[i].Item[medida][entorno])
            )
            .flat()
            .reverse()
        );
      }

      labels.reverse();

      if (i18n.language === 'en') {
        labels = labels.map((label: string) => label.replace(/^s-/, 'w-'));
      }

      values.forEach((val, i) => {
        series.push({
          name: translateDataLabel(datalabels[i]),
          data: val,
        });
      });

      
      exportData = getExportData(
        data,
        datalabels.map(translateDataLabel),
        labels,
        semanas[0].year === 2019,
        t
      );
    } catch (err: any) {
      error = true;
      errorMsg = err.message ?? errorMsg;
    }
  }

  const max: number | undefined = useMemo(() => getMax(values), [values]);
  const min: number | undefined = useMemo(() => getMin(values), [values]);
  const stepSize = useMemo(() => getSteps(min, max), [min, max]);

  const graphData = {
    datalabels: datalabels.map(translateDataLabel),
    labels: labels,
    values: values,
    colors: [
      "#007070",
      "#A30041",
      "#85b709",
      "#888888",
      "#363aaf",
      "#9a2d79",
      "#4d947a",
      "#7EA3CF",
      "#DC9E9D",
    ],
    annotation: [],
    series: series,
    min: min,
    max: max,
    stepSize: stepSize,
  };

  return (
    <>
      <Title
        title={title}
        error={!!error}
        files={{
          data: exportData,
          semanas,
          user,
          title,
          leyenda: t("Period") as string,
        }}
      />
      {error && <MessageBar isMultiline={true}>{errorMsg}</MessageBar>}
      {loading && (
        <div className="loading-graph">
          <Spinner size={SpinnerSize.large} />
        </div>
      )}
      {!error && (
        <LineGraph
          data={graphData}
          hue={320}
          annotation={"#00ff00"}
          percentages
        />
      )}
    </>
  );
};

export default Graph;


const getExportData = (
  data: any,
  datalabels: any[],
  labels: any,
  isLeap: boolean,
  t: (k: string) => string
) => {
  const exportData: any[] = [];


  const measureMap: Record<string, string> = {
    Ventas: t("Sales"),
    Tickets: t("Tickets"),
  };
  const scopeMap: Record<string, string> = {
    Totales: t("Totals"),
    Comparables: t("Comparables"),
  };

  for (let i = 0; i < datalabels.length; i++) {
    const tempVals: any = {};
    ["Ventas", "Tickets"].forEach((med: string) => {
      const scopes = isLeap ? ["Totales"] : ["Totales", "Comparables"];
      scopes.forEach((ent: string) => {
        const tempVal = data
          .map((dat: any) =>
            Object.values(dat).map((d: any) => d[i].Item[med][ent])
          )
          .flat()
          .reverse();


        const translatedHeader = `${measureMap[med]} ${scopeMap[ent]}`;
        tempVals[translatedHeader] = { ...mergeKeyData(labels, tempVal) };
      });
    });

    exportData.push({ [datalabels[i]]: tempVals });
  }

  return exportData;
};